import { CloseOutlined } from '@ant-design/icons';
import { useTranslate } from '@refinedev/core';
import { Button, Space, Typography } from 'antd';
import React from 'react';
import {
  openWhatsNewArticle,
  useAcknowledgeWhatsNew,
  usePendingWhatsNew,
} from './useWhatsNew';
import { WhatsNewImage } from './WhatsNewImage';

/**
 * E32 · "Portal Dashboard — What's New banner (section)" — a dismissible CARD above the page
 * content, never a modal, one variant per announcement in the therapist's language (founder
 * 2026-09-21). It shows the newest pending announcement; the rest are in the drawer. Hidden when
 * nothing is pending.
 *
 * Mounted in the themed layout above every authenticated page rather than on one route: the
 * portal's landing is the client list and a therapist may deep-link anywhere, so "the dashboard"
 * for this purpose is the shell's content area — the same reasoning that puts the E30 update
 * banner on the whole tab.
 *
 * Dismiss → `AcknowledgeFeatureAnnouncement` (against the announcement, `seenAs` this variant) and
 * a re-read; the card leaves as soon as the server has the fact, so a failed send keeps it visible
 * rather than losing it. The article opens in a new tab with the E04 usage emit.
 */
export const WhatsNewBanner: React.FC = () => {
  const translate = useTranslate();
  const { rows, refetch } = usePendingWhatsNew();
  const { acknowledge, isLoading } = useAcknowledgeWhatsNew();
  const announcement = rows[0];

  if (!announcement) {
    return null;
  }

  const dismiss = async () => {
    try {
      await acknowledge(announcement);
    } finally {
      await refetch();
    }
  };

  return (
    <div
      data-testid="cc-whats-new-banner"
      role="status"
      style={{
        background: '#F1EDE9',
        border: '1px solid #C2C8C0',
        borderRadius: 12,
        margin: '0 0 16px',
        padding: '14px 16px',
      }}
    >
      <div
        style={{
          alignItems: 'flex-start',
          display: 'flex',
          gap: 12,
          justifyContent: 'space-between',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <Typography.Text
            data-testid="cc-whats-new-banner-eyebrow"
            style={{
              color: '#4A654E',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
            }}
          >
            {translate('whatsNew.title')}
          </Typography.Text>
          <WhatsNewImage
            alt={announcement.title}
            mediaUrl={announcement.mediaUrl}
            testId="cc-whats-new-banner-image"
          />
          <Typography.Title
            data-testid="cc-whats-new-banner-title"
            level={5}
            style={{ margin: '4px 0 2px' }}
          >
            {announcement.title}
          </Typography.Title>
          <Typography.Paragraph style={{ margin: 0 }} type="secondary">
            {announcement.summary}
          </Typography.Paragraph>
          {announcement.relatedReleases.length > 0 ? (
            <Typography.Text
              style={{ display: 'block', fontSize: 12, marginTop: 4 }}
              type="secondary"
            >
              {translate('whatsNew.releases', {
                versions: announcement.relatedReleases.join(', '),
              })}
            </Typography.Text>
          ) : null}
          <Space style={{ marginTop: 10 }}>
            {announcement.articleUrl ? (
              <Button
                data-testid="cc-whats-new-banner-read"
                onClick={() => openWhatsNewArticle(announcement)}
                size="small"
                type="primary"
              >
                {translate('whatsNew.readArticle')}
              </Button>
            ) : null}
            <Button
              data-testid="cc-whats-new-banner-dismiss"
              loading={isLoading}
              onClick={dismiss}
              size="small"
            >
              {translate('whatsNew.dismiss')}
            </Button>
          </Space>
        </div>
        <Button
          aria-label={translate('whatsNew.dismiss')}
          data-testid="cc-whats-new-banner-close"
          icon={<CloseOutlined />}
          onClick={dismiss}
          size="small"
          type="text"
        />
      </div>
    </div>
  );
};
